import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { bus } from "../lib/broadcast";
import { toast } from "sonner";
import { fetchSubmissions, fetchReactionStats, recordHistory, fetchHistory, recordLibraryUse, reportBlockedVideo, searchYouTube, setNowSinger } from "../lib/api";

const KaraokeCtx = createContext(null);

const STORAGE_KEY = "karaoke_state_v1";

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to load persisted karaoke state", e);
  }
  return { queue: [], currentIndex: -1, volume: 80, secondScreenOpen: false };
};

// Only the DJ console tab drives playback; /guest and /player also mount this provider.
const IS_CONSOLE = typeof window !== "undefined" && window.location.pathname === "/";

export const KaraokeProvider = ({ children }) => {
  const initial = loadInitial();
  const [queue, setQueue] = useState(initial.queue || []);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [presenting, setPresenting] = useState(null); // singer name being announced
  const [volume, setVolume] = useState(initial.volume ?? 80);
  const [secondScreenOpen, setSecondScreenOpen] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [qrActive, setQrActive] = useState(false);
  const [restartSignal, setRestartSignal] = useState(0);
  const [playNowSong, setPlayNowSong] = useState(null); // temporary "Reproducir ahora" song, lives OUTSIDE the queue
  const [injectedSearch, setInjectedSearch] = useState(null); // { query, nonce } for DJ "Buscar"
  const [searchRequestContext, setSearchRequestContext] = useState(null); // { requestId, singer, dedication, query }
  const [recoveryContext, setRecoveryContext] = useState(null); // { reason, title, cleanQuery, singer, dedication, requestId, currentVideoId, invalidVideoIds } when a version needs replacing
  const [recoveryAlternative, setRecoveryAlternative] = useState(null); // best local alternative version (no YouTube quota)
  const [recoveryPrompt, setRecoveryPrompt] = useState(0); // nonce: bumped to (re)open the recovery flow
  const secondWindowRef = useRef(null);
  const presentationTimer = useRef(null);
  const presentationWorker = useRef(null);
  const seenSubmissionIds = useRef(new Set());
  const lastHeartbeat = useRef(0);
  const wakeLock = useRef(null);
  const currentSongStartedAt = useRef(null);
  const ratingTimer = useRef(null);
  const playNextRef = useRef(null);
  const endPresentationRef = useRef(null); // latest presentation's end handler (for skip)
  const presentationCancelRef = useRef(null); // cancels the pending presentation (on unplayable video)
  const unplayableRef = useRef(null); // latest handleUnplayable (called by bus listener)

  // Lazy-init the presentation timer worker (unaffected by tab throttling)
  const ensureWorker = useCallback(() => {
    if (presentationWorker.current) return presentationWorker.current;
    try {
      presentationWorker.current = new Worker("/timer-worker.js");
    } catch (_) {
      presentationWorker.current = null;
    }
    return presentationWorker.current;
  }, []);

  // Poll for guest submissions every 4s
  const refreshSubmissions = useCallback(async () => {
    try {
      const subs = await fetchSubmissions("pending");
      setPendingSubmissions(subs);
      return subs;
    } catch (_) {
      return [];
    }
  }, []);

  useEffect(() => {
    refreshSubmissions();
    const interval = setInterval(refreshSubmissions, 4000);
    return () => clearInterval(interval);
  }, [refreshSubmissions]);

  // Detect newly-arrived submissions and notify
  useEffect(() => {
    pendingSubmissions.forEach((s) => {
      if (!seenSubmissionIds.current.has(s.id)) {
        seenSubmissionIds.current.add(s.id);
        // Skip notifying for submissions seen on first load
        if (seenSubmissionIds.current.size > pendingSubmissions.length) {
          // toast handled outside via subscriber to keep store clean
        }
      }
    });
  }, [pendingSubmissions]);

  // Persist queue/volume
  useEffect(() => {
    if (!IS_CONSOLE) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ queue, volume }));
  }, [queue, volume]);

  // Broadcast current state to second screen
  const broadcastState = useCallback(
    (extra = {}) => {
      bus.post({
        type: "state",
        queue,
        currentIndex,
        playNowSong,
        isPlaying,
        presenting,
        volume,
        ...extra,
      });
    },
    [queue, currentIndex, playNowSong, isPlaying, presenting, volume]
  );

  useEffect(() => {
    if (IS_CONSOLE) broadcastState();
  }, [broadcastState]);

  // Wake Lock: prevent the screen from sleeping while music is playing
  useEffect(() => {
    let canceled = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && isPlaying) {
          const lock = await navigator.wakeLock.request("screen");
          if (canceled) {
            lock.release();
            return;
          }
          wakeLock.current = lock;
          lock.addEventListener("release", () => {
            wakeLock.current = null;
          });
        }
      } catch (e) {
        console.error("WakeLock acquire failed", e);
      }
    };
    const release = async () => {
      try {
        if (wakeLock.current) {
          await wakeLock.current.release();
          wakeLock.current = null;
        }
      } catch (e) {
        console.error("WakeLock release failed", e);
      }
    };
    if (isPlaying) acquire();
    else release();

    // Re-acquire on visibilitychange (lock auto-releases when tab is hidden)
    const onVisible = () => {
      if (document.visibilityState === "visible" && isPlaying && !wakeLock.current) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      canceled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isPlaying]);

  // Refs to functions defined later, so the bus listener can call them without
  // needing to be re-created on each render.
  const advanceRef = useRef(null);

  // Listen for second-screen ready and replay state
  useEffect(() => {
    if (!IS_CONSOLE) return undefined;
    return bus.on((msg) => {
      if (msg?.type === "request-state") {
        broadcastState();
      }
      if (msg?.type === "ended-from-second") {
        advanceRef.current?.();
      }
      if (msg?.type === "unplayable") {
        // Second screen detected a blocked/unplayable video -> recover, do NOT advance.
        unplayableRef.current?.(msg.reason || "blocked");
      }
      // Second screen heartbeat (sent every 2s by SecondScreen)
      if (msg?.type === "second-alive") {
        setSecondScreenOpen(true);
        lastHeartbeat.current = Date.now();
      }
      if (msg?.type === "second-closed") {
        setSecondScreenOpen(false);
        lastHeartbeat.current = 0;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastState]);

  // Heartbeat watcher: if no signal for >5s, mark second screen as closed
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastHeartbeat.current && Date.now() - lastHeartbeat.current > 5000) {
        setSecondScreenOpen(false);
        lastHeartbeat.current = 0;
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const currentSong = playNowSong || (currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null);

  // Publish the singer on stage so /guest can label "VOTAR A [NOMBRE]".
  const nowSingerName = currentSong ? currentSong.singer || "Invitado" : null;
  useEffect(() => {
    if (!IS_CONSOLE) return;
    setNowSinger(nowSingerName).catch(() => {});
  }, [nowSingerName]);


  const addToQueue = (song, singer, dedication = null) => {
    // Validation: never allow a song without a valid videoId into the queue
    if (!song || !song.videoId) {
      toast.error("Esta solicitud no tiene un video válido", {
        description: "Usa 'Buscar' para encontrar una versión reproducible antes de agregar.",
      });
      return false;
    }
    setQueue((q) => [
      ...q,
      { ...song, singer, dedication, played: false, id: `${song.videoId}-${Date.now()}` },
    ]);
    // Persist to the all-time song history log (fire-and-forget)
    recordHistory({
      videoId: song.videoId,
      title: song.title,
      channelTitle: song.channelTitle,
      singer,
    }).catch((e) => console.error("Failed to record song history", e));
    return true;
  };

  // DJ presses "Buscar" on a search_request: inject the query into the console SearchPanel
  // and remember the requester info so AddToQueueDialog can be prefilled + the request resolved.
  const startSearchRequest = (sub) => {
    setSearchRequestContext({
      requestId: sub.id,
      singer: sub.singer || "",
      dedication: sub.dedication || "",
      query: sub.query || sub.title || "",
    });
    setInjectedSearch({ query: sub.query || sub.title || "", nonce: Date.now() });
  };

  const clearSearchRequestContext = () => setSearchRequestContext(null);

  const removeFromQueue = (id) => {
    setQueue((q) => {
      const idx = q.findIndex((s) => s.id === id);
      if (idx === -1) return q;
      const newQ = q.filter((s) => s.id !== id);
      if (idx < currentIndex) setCurrentIndex((i) => i - 1);
      else if (idx === currentIndex) {
        // current removed
        setIsPlaying(false);
        setCurrentIndex(-1);
      }
      return newQ;
    });
  };

  const moveItem = (id, direction) => {
    setQueue((q) => {
      const idx = q.findIndex((s) => s.id === id);
      if (idx === -1) return q;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= q.length) return q;
      const newQ = [...q];
      [newQ[idx], newQ[newIdx]] = [newQ[newIdx], newQ[idx]];
      // adjust currentIndex
      if (currentIndex === idx) setCurrentIndex(newIdx);
      else if (currentIndex === newIdx) setCurrentIndex(idx);
      return newQ;
    });
  };

  const clearQueue = () => {
    setQueue([]);
    setCurrentIndex(-1);
    setIsPlaying(false);
  };

  // Shared presentation → deferred playback routine (used by both queue playback
  // and "Reproducir ahora"). `onStart` runs exactly once, when playback actually
  // begins (after the 10s banner or when the DJ skips it).
  const presentAndPlay = (song, index, onStart) => {
    setPresenting({ singer: song.singer || song.title, dedication: song.dedication || null });
    bus.post({
      type: "present",
      singer: song.singer || song.title,
      dedication: song.dedication || null,
      song,
      index,
    });

    if (presentationTimer.current) clearTimeout(presentationTimer.current);

    let fired = false;
    let canceled = false;
    const endPresentation = () => {
      if (fired || canceled) return;
      fired = true;
      if (presentationTimer.current) clearTimeout(presentationTimer.current);
      setPresenting(null);
      setIsPlaying(true);
      currentSongStartedAt.current = new Date().toISOString();
      bus.post({ type: "play", song, index });
      // A song actually starting = the recovery cycle (if any) is resolved.
      setRecoveryContext(null);
      setRecoveryAlternative(null);
      bus.post({ type: "recovery", active: false });
      if (onStart) onStart();
      else recordLibraryUse(song, "played", song.singer || song.title).catch(() => {});
    };
    endPresentationRef.current = endPresentation;
    presentationCancelRef.current = () => {
      canceled = true;
      if (presentationTimer.current) clearTimeout(presentationTimer.current);
    };

    // Primary: Web Worker timer (not throttled when tab is hidden)
    const w = ensureWorker();
    if (w) {
      const tickId = `present-${Date.now()}-${index}`;
      const handler = (e) => {
        if (e.data?.type === "tick" && e.data.id === tickId) {
          w.removeEventListener("message", handler);
          endPresentation();
        }
      };
      w.addEventListener("message", handler);
      w.postMessage({ type: "start", delay: 10000, id: tickId });
    }
    // Fallback: setTimeout in main thread (works in foreground)
    presentationTimer.current = setTimeout(endPresentation, 10000);
  };

  const playIndex = (idx) => {
    if (idx < 0 || idx >= queue.length) return;
    const song = queue[idx];
    if (song.played) return; // disabled: already played, must be re-enabled first
    setPlayNowSong(null);
    setCurrentIndex(idx);
    setIsPlaying(false);
    presentAndPlay(song, idx);
  };

  // "Reproducir ahora": start the chosen song immediately WITHOUT mutating the
  // queue. The interrupted queue song is only marked as played AFTER the new
  // song actually begins, so a failed/invalid pick never disturbs the queue.
  const playNow = (rawSong, singer, dedication = null) => {
    if (!rawSong || !rawSong.videoId) {
      toast.error("Esta canción no tiene un video válido", {
        description: "No se puede reproducir ahora. Elige otra versión.",
      });
      return false;
    }
    const interruptedIdx = currentIndex; // queue index active right now (or -1 if none / another play-now)
    const song = {
      ...rawSong,
      singer: singer || rawSong.singer || null,
      dedication: dedication ?? null,
      played: false,
      id: `${rawSong.videoId}-now-${Date.now()}`,
    };
    setPlayNowSong(song);
    setCurrentIndex(-1);
    setIsPlaying(false);
    presentAndPlay(song, -1, () => {
      // Runs only once the new song has actually started playing.
      // Mark ONLY the interrupted queue item (if any) as played so it won't
      // return on its own. Pending songs are never touched.
      if (interruptedIdx >= 0) {
        setQueue((q) => q.map((s, i) => (i === interruptedIdx ? { ...s, played: true } : s)));
      }
      recordHistory({
        videoId: song.videoId,
        title: song.title,
        channelTitle: song.channelTitle,
        singer: song.singer,
      }).catch((e) => console.error("Failed to record song history", e));
      recordLibraryUse(song, "played", song.singer || song.title).catch(() => {});
    });
    return true;
  };

  // Build a clean search query from a polluted karaoke video title.
  const cleanTitleForSearch = (title = "") => {
    let t = ` ${String(title).toLowerCase()} `;
    t = t.replace(/\([^)]*\)/g, " ").replace(/\[[^\]]*\]/g, " ");
    t = t.replace(/\b(karaoke|instrumental|lyrics|letra|letras|version|versi[oó]n|cover|hd|hq|4k|official|oficial|video|videoclip|pista|backing\s*track|demo|remaster(ed)?|con\s*coros|sin\s*coros|acoustic|ac[uú]stic[oa]|piano|minus\s*one|playback)\b/g, " ");
    t = t.replace(/[|/–—_-]+/g, " ").replace(/\s+/g, " ").trim();
    return t || String(title);
  };

  // Single unified recovery/replacement flow. reason:
  //  - "blocked_video": the version IS marked invalid (embed disabled / unplayable).
  //  - "manual_replace": DJ wants another version (voces, calidad, etc.) — NOT invalidated.
  // In both cases: stop, do NOT advance, do NOT complete the singer turn, keep the
  // rest of the queue intact, preserve singer/dedication/request, then offer a local
  // alternative (no quota) and/or a forced search.
  const enterRecovery = async (reason) => {
    const target = currentSong;
    if (!target) return;
    const isBlocked = reason === "blocked_video";
    if (isBlocked && target.videoId) {
      reportBlockedVideo({ videoId: target.videoId, reason, title: target.title }).catch(() => {});
    }
    // Cancel any pending presentation so it never "starts" this version.
    if (presentationCancelRef.current) presentationCancelRef.current();
    // Remove the current version (queue item or play-now) WITHOUT marking it played.
    if (playNowSong) {
      setPlayNowSong(null);
    } else if (currentIndex >= 0) {
      setQueue((q) => q.filter((_, i) => i !== currentIndex));
    }
    setCurrentIndex(-1);
    setIsPlaying(false);
    setPresenting(null);
    if (presentationTimer.current) clearTimeout(presentationTimer.current);
    currentSongStartedAt.current = null;
    bus.post({ type: "stop" });

    const prev = recoveryContext;
    const ctx = {
      reason,
      title: target.title || prev?.title || "",
      cleanQuery: prev?.cleanQuery || target.query || target.original_query || cleanTitleForSearch(target.title || ""),
      singer: target.singer || prev?.singer || "Invitado",
      dedication: target.dedication ?? prev?.dedication ?? null,
      requestId: target.requestId || target.request_id || prev?.requestId || null,
      currentVideoId: target.videoId || null,
      // Only invalidated (blocked) versions accumulate; a manual replace is excluded via currentVideoId.
      invalidVideoIds: [...(prev?.invalidVideoIds || []), isBlocked ? target.videoId : null].filter(Boolean),
    };
    setRecoveryContext(ctx);
    // P3: discreet second-screen notice while recovering.
    bus.post({ type: "recovery", active: true, singer: ctx.singer });

    // P2: look for the best LOCAL alternative (favoritos/biblioteca/caché/índice), no YouTube quota.
    let alternative = null;
    try {
      const data = await searchYouTube(ctx.cleanQuery || ctx.title, { maxResults: 6, localOnly: true, excludeId: ctx.currentVideoId });
      const excluded = new Set([ctx.currentVideoId, ...ctx.invalidVideoIds].filter(Boolean));
      alternative = (data.results || []).find((r) => r.videoId && !excluded.has(r.videoId)) || null;
    } catch (_) {
      /* local check never blocks recovery */
    }
    setRecoveryAlternative(alternative);
    setRecoveryPrompt((n) => n + 1);
  };

  // Blocked/unplayable video (auto): invalidates the version.
  const handleUnplayable = () => enterRecovery("blocked_video");
  unplayableRef.current = handleUnplayable;

  // DJ pressed "Buscar otra versión" (manual): does NOT invalidate the version.
  const requestReplacement = () => enterRecovery("manual_replace");

  // DJ chose to search: reuse the console SearchPanel (prefill + forced search),
  // carrying the original singer/dedication/request into AddToQueueDialog, and
  // excluding the current version (manual replace isn't invalidated).
  const startRecoverySearch = () => {
    if (!recoveryContext) return;
    const query = recoveryContext.cleanQuery || recoveryContext.title || "";
    setSearchRequestContext({
      requestId: recoveryContext.requestId || null,
      singer: recoveryContext.singer || "",
      dedication: recoveryContext.dedication || "",
      query,
      recovery: true,
    });
    setRecoveryAlternative(null);
    setInjectedSearch({
      query,
      nonce: Date.now(),
      excludeId: recoveryContext.reason === "manual_replace" ? recoveryContext.currentVideoId : null,
    });
  };

  const clearRecoveryContext = () => {
    setRecoveryContext(null);
    setRecoveryAlternative(null);
    bus.post({ type: "recovery", active: false });
  };

  // STOP (manual): stop + clear the current player WITHOUT advancing. Pending queue
  // stays intact; the stopped queue item reuses the "played" (interrupted) state.
  const stopPlayback = () => {
    if (presentationCancelRef.current) presentationCancelRef.current();
    if (presentationTimer.current) clearTimeout(presentationTimer.current);
    if (ratingTimer.current) clearTimeout(ratingTimer.current);
    if (playNowSong) setPlayNowSong(null);
    else if (currentIndex >= 0) {
      const idx = currentIndex;
      setQueue((q) => q.map((s, i) => (i === idx ? { ...s, played: true } : s)));
    }
    setCurrentIndex(-1);
    setIsPlaying(false);
    setPresenting(null);
    currentSongStartedAt.current = null;
    if (recoveryContext) clearRecoveryContext();
    bus.post({ type: "hide-rating" });
    bus.post({ type: "stop" });
  };

  const advanceWithRating = useCallback(async () => {
    const startedAt = currentSongStartedAt.current;
    currentSongStartedAt.current = null;
    if (startedAt) {
      try {
        const stats = await fetchReactionStats(startedAt, "vote");
        if (stats?.count > 0) {
          bus.post({
            type: "show-rating",
            average: stats.average,
            count: stats.count,
            min: stats.min,
            max: stats.max,
          });
          await new Promise((r) => {
            ratingTimer.current = setTimeout(r, 3500);
          });
          bus.post({ type: "hide-rating" });
        }
      } catch (e) {
        console.error("Failed to fetch rating stats", e);
      }
    }
    playNextRef.current?.();
  }, []);

  // Keep refs in sync so listeners always call the latest implementation
  advanceRef.current = advanceWithRating;

  const playNext = () => {
    // If a temporary "Reproducir ahora" song just finished, drop it (it never
    // lived in the queue) and continue with the first pending song in the
    // original order. No queue item is marked here.
    if (playNowSong) {
      setPlayNowSong(null);
      setCurrentIndex(-1);
      const nextIdx = queue.findIndex((s) => !s.played);
      if (nextIdx >= 0) playIndex(nextIdx);
      else {
        setIsPlaying(false);
        setPresenting(null);
        bus.post({ type: "stop" });
      }
      return;
    }
    // Mark the song that just finished/was skipped as played (struck out, disabled)
    const curIdx = currentIndex;
    if (curIdx >= 0 && curIdx < queue.length) {
      setQueue((q) => q.map((s, i) => (i === curIdx ? { ...s, played: true } : s)));
    }
    // Advance to the next song that has NOT been played yet
    const nextIdx = queue.findIndex((s, i) => i > curIdx && !s.played);
    if (nextIdx >= 0) playIndex(nextIdx);
    else {
      setIsPlaying(false);
      setPresenting(null);
      bus.post({ type: "stop" });
    }
  };
  playNextRef.current = playNext;

  const playPrev = () => {
    // Go back to the nearest previous song that is still enabled
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!queue[i].played) {
        playIndex(i);
        return;
      }
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      bus.post({ type: "pause" });
      return;
    }
    // Currently presenting → skip the presentation and start the video
    if (presenting) {
      skipPresentation();
      return;
    }
    // Resume a song that is paused mid-playback (not yet finished)
    if (currentSong && !currentSong.played) {
      setIsPlaying(true);
      bus.post({ type: "resume" });
      return;
    }
    // Otherwise start the first enabled (not-yet-played) song in the queue.
    // This is what makes a freshly-added song play after the whole list has
    // already been played, instead of restarting the list from the top.
    const idx = queue.findIndex((s) => !s.played);
    if (idx >= 0) playIndex(idx);
  };

  // Toggle the "played"/disabled state of a queue item (re-enable button)
  const togglePlayed = (id) => {
    setQueue((q) => q.map((s) => (s.id === id ? { ...s, played: !s.played } : s)));
  };

  const skipPresentation = () => {
    // Fire the current presentation's end handler once (starts playback + runs
    // its onStart side-effects), instead of duplicating that logic here.
    if (endPresentationRef.current) endPresentationRef.current();
  };

  // Restart the current song from the beginning on both screens, WITHOUT
  // re-triggering the presentation banner.
  const restartSong = () => {
    if (!currentSong || currentSong.played) return;
    setIsPlaying(true);
    setRestartSignal((n) => n + 1); // local signal for the main PlayerPanel
    bus.post({ type: "restart" }); // second screen restart
  };

  const openSecondScreen = async () => {
    // Try Window Management API for second monitor detection
    let left = 100;
    let top = 100;
    let width = 1280;
    let height = 720;
    try {
      if ("getScreenDetails" in window) {
        // eslint-disable-next-line no-undef
        const details = await window.getScreenDetails();
        const otherScreen = details.screens.find((s) => !s.isPrimary) || details.screens[1];
        if (otherScreen) {
          left = otherScreen.availLeft;
          top = otherScreen.availTop;
          width = otherScreen.availWidth;
          height = otherScreen.availHeight;
        }
      }
    } catch (e) {
      console.warn("Window Management API not available", e);
    }
    const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;
    const win = window.open("/player", "KaraokePlayer", features);
    secondWindowRef.current = win;
    setSecondScreenOpen(true);
    // Resend state
    setTimeout(() => broadcastState(), 800);
  };

  const closeSecondScreen = () => {
    if (secondWindowRef.current && !secondWindowRef.current.closed) {
      secondWindowRef.current.close();
    }
    secondWindowRef.current = null;
    setSecondScreenOpen(false);
  };

  const toggleSecondScreen = () => {
    if (secondScreenOpen) closeSecondScreen();
    else openSecondScreen();
  };

  const requestSecondFullscreen = () => {
    const win = secondWindowRef.current;
    if (win && !win.closed) {
      try {
        // Capability Delegation: transfer fullscreen activation to the 2nd window
        win.postMessage(
          { type: "request-fullscreen" },
          { targetOrigin: window.location.origin, delegate: "fullscreen" }
        );
        return;
      } catch (e) {
        // Older browsers: fall through to BroadcastChannel fallback
        try {
          win.postMessage({ type: "request-fullscreen" }, window.location.origin);
        } catch (err) {
          console.error("Fullscreen delegation fallback failed", err);
        }
      }
    }
    // Fallback if we don't hold a direct reference (e.g. after refresh)
    bus.post({ type: "request-fullscreen" });
  };

  const guestUrl = typeof window !== "undefined" ? `${window.location.origin}/guest` : "/guest";

  const toggleQr = () => {
    setQrActive((active) => {
      const next = !active;
      if (next) bus.post({ type: "show-qr", url: guestUrl });
      else bus.post({ type: "hide-qr" });
      return next;
    });
  };

  // Auto-broadcast QR state whenever the 2nd screen reconnects
  useEffect(() => {
    if (qrActive && secondScreenOpen) {
      bus.post({ type: "show-qr", url: guestUrl });
    }
  }, [qrActive, secondScreenOpen, guestUrl]);

  const loadFromPlaylist = (songs) => {
    setQueue(songs.map((s) => ({ ...s, played: false, id: s.id || `${s.videoId}-${Date.now()}-${Math.random()}` })));
    setCurrentIndex(-1);
    setIsPlaying(false);
  };

  // Download the all-time song history as a plain-text file
  const downloadHistory = async () => {
    const entries = await fetchHistory();
    const generatedAt = new Date().toLocaleString("es");
    const lines = entries.map((e, i) => {
      const date = e.addedAt ? new Date(e.addedAt).toLocaleString("es") : "";
      const singer = e.singer || "—";
      return `${String(i + 1).padStart(3, "0")}. ${e.title}\n     Cantante: ${singer}  |  ${date}\n     ${e.link}`;
    });
    const text =
      "DJ GABO KARAOKE — Historial de canciones\n" +
      `Total: ${entries.length} canciones  |  Generado: ${generatedAt}\n` +
      `${"=".repeat(60)}\n\n` +
      (lines.length ? lines.join("\n\n") : "Aún no hay canciones en el historial.") +
      "\n";
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `historial-karaoke-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return entries.length;
  };

  const value = {
    queue,
    currentIndex,
    currentSong,
    playNowSong,
    isPlaying,
    presenting,
    volume,
    setVolume,
    secondScreenOpen,
    addToQueue,
    removeFromQueue,
    moveItem,
    clearQueue,
    playIndex,
    playNow,
    togglePlayed,
    advanceWithRating,
    playNext,
    playPrev,
    togglePlay,
    skipPresentation,
    restartSong,
    restartSignal,
    toggleSecondScreen,
    requestSecondFullscreen,
    qrActive,
    toggleQr,
    guestUrl,
    loadFromPlaylist,
    downloadHistory,
    setIsPlaying,
    pendingSubmissions,
    refreshSubmissions,
    injectedSearch,
    searchRequestContext,
    startSearchRequest,
    clearSearchRequestContext,
    recoveryContext,
    recoveryAlternative,
    recoveryPrompt,
    handleUnplayable,
    requestReplacement,
    startRecoverySearch,
    clearRecoveryContext,
    stopPlayback,
  };

  return <KaraokeCtx.Provider value={value}>{children}</KaraokeCtx.Provider>;
};

export const useKaraoke = () => {
  const ctx = useContext(KaraokeCtx);
  if (!ctx) throw new Error("useKaraoke must be used inside KaraokeProvider");
  return ctx;
};
